import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  HStack,
  Input,
  Text,
  VStack,
  Spinner,
  Badge,
} from '@chakra-ui/react';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { baseUrl } from '../../../hooks/useEndpoints';

// ─── Tipi ────────────────────────────────────────────────────────────

export interface ITeamCustomMetric {
  canonicalKey: string;
  label: string;
  unit: string;
  category: string;
  level: 'session' | 'per_athlete';
}

// ─── Categorie disponibili (allineate a IMetricDef.group) ───────────

const METRIC_CATEGORIES: { value: string; label: string }[] = [
  { value: 'volume', label: 'Volume' },
  { value: 'internal', label: 'Carico interno' },
  { value: 'metabolic', label: 'Metabolico' },
  { value: 'advanced', label: 'Avanzato' },
  { value: 'running', label: 'Running' },
  { value: 'cycling', label: 'Cycling' },
  { value: 'endurance_common', label: 'Endurance comune' },
  { value: 'identification', label: 'Identificazione' },
  { value: 'events', label: 'Events' },
  { value: 'custom', label: 'Custom' },
];

// ─── Helpers ──────────────────────────────────────────────────────────

function toSlug(label: string): string {
  return label
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

// ─── API ──────────────────────────────────────────────────────────────

const teamCustomMetricsApi = {
  get: (teamId: string) =>
    axios.get<ITeamCustomMetric[]>(`${baseUrl}/teams/${teamId}/custom-metrics`),
  add: (teamId: string, metric: ITeamCustomMetric) =>
    axios.post<ITeamCustomMetric[]>(`${baseUrl}/teams/${teamId}/custom-metrics`, metric),
  remove: (teamId: string, canonicalKey: string) =>
    axios.delete<ITeamCustomMetric[]>(
      `${baseUrl}/teams/${teamId}/custom-metrics/${canonicalKey}`,
    ),
};

// ─── Form aggiunta ────────────────────────────────────────────────────

interface AddMetricFormProps {
  teamId: string;
  onSuccess: () => void;
}

const AddMetricForm: React.FC<AddMetricFormProps> = ({ teamId, onSuccess }) => {
  const [label, setLabel] = useState('');
  const [unit, setUnit] = useState('');
  const [category, setCategory] = useState('custom');
  const [level, setLevel] = useState<'session' | 'per_athlete'>('session');

  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (metric: ITeamCustomMetric) =>
      teamCustomMetricsApi.add(teamId, metric),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-custom-metrics', teamId] });
      toast.success('Metrica aggiunta');
      setLabel('');
      setUnit('');
      setCategory('custom');
      setLevel('session');
      onSuccess();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Errore durante l\'aggiunta');
    },
  });

  const handleAdd = useCallback(() => {
    const trimLabel = label.trim();
    const trimUnit = unit.trim();
    if (!trimLabel || !trimUnit) {
      toast.error('Label e unità di misura sono obbligatorie');
      return;
    }
    const canonicalKey = toSlug(trimLabel);
    if (!canonicalKey) {
      toast.error('Label non valida per generare un canonicalKey');
      return;
    }
    mutation.mutate({ canonicalKey, label: trimLabel, unit: trimUnit, category, level });
  }, [label, unit, category, level, mutation]);

  return (
    <Box
      p={4}
      bg="gray.50"
      borderRadius="md"
      border="1px solid"
      borderColor="gray.200"
    >
      <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
        Aggiungi metrica custom
      </Text>

      <VStack align="stretch" gap={3}>
        <HStack gap={3}>
          <VStack align="stretch" flex={2} gap={1}>
            <Text fontSize="xs" color="gray.600" fontWeight="medium">Label *</Text>
            <Input
              size="sm"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="es. Distanza HSR custom"
            />
          </VStack>
          <VStack align="stretch" flex={1} gap={1}>
            <Text fontSize="xs" color="gray.600" fontWeight="medium">Unità *</Text>
            <Input
              size="sm"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              placeholder="es. m, km, W"
            />
          </VStack>
        </HStack>

        <HStack gap={3}>
          <VStack align="stretch" flex={1} gap={1}>
            <Text fontSize="xs" color="gray.600" fontWeight="medium">Categoria</Text>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid #e2e8f0',
                fontSize: 13,
                fontFamily: 'inherit',
                background: 'var(--c-card-bg)',
              }}
            >
              {METRIC_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </VStack>

          <VStack align="stretch" flex={1} gap={1}>
            <Text fontSize="xs" color="gray.600" fontWeight="medium">Livello</Text>
            <HStack gap={2}>
              {(['session', 'per_athlete'] as const).map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLevel(l)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 6,
                    border: `1px solid ${level === l ? '#013344' : '#e2e8f0'}`,
                    background: level === l ? '#013344' : 'white',
                    color: level === l ? 'white' : '#4d4d4d',
                    fontSize: 12,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    transition: 'all 0.15s',
                  }}
                >
                  {l === 'session' ? 'Sessione' : 'Per atleta'}
                </button>
              ))}
            </HStack>
          </VStack>
        </HStack>

        {label && (
          <Text fontSize="xs" color="gray.500">
            canonicalKey generato: <strong>{toSlug(label) || '—'}</strong>
          </Text>
        )}

        <Button
          size="sm"
          colorPalette="green"
          onClick={handleAdd}
          loading={mutation.isPending}
          loadingText="Aggiunta..."
          alignSelf="flex-start"
        >
          <HStack gap={1}>
            <PlusIcon size={14} />
            <Text>Aggiungi</Text>
          </HStack>
        </Button>
      </VStack>
    </Box>
  );
};

// ─── Props componente principale ──────────────────────────────────────

interface TeamCustomMetricsSectionProps {
  teamId: string;
}

/**
 * Sezione CRUD metriche custom del team (M6.2 B13).
 * - Lista metriche esistenti con badge categoria/livello e delete
 * - Form aggiunta con slug auto-generato da label
 * Integrato in DetailTeamPage tab Impostazioni o Metriche custom.
 */
export const TeamCustomMetricsSection: React.FC<TeamCustomMetricsSectionProps> = ({
  teamId,
}) => {
  const [showForm, setShowForm] = useState(false);
  const qc = useQueryClient();

  const { data: metrics = [], isLoading, isError } = useQuery<ITeamCustomMetric[]>({
    queryKey: ['team-custom-metrics', teamId],
    queryFn: async () => {
      const res = await teamCustomMetricsApi.get(teamId);
      return res.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  const removeMutation = useMutation({
    mutationFn: (canonicalKey: string) =>
      teamCustomMetricsApi.remove(teamId, canonicalKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-custom-metrics', teamId] });
      toast.success('Metrica rimossa');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Errore durante la rimozione');
    },
  });

  return (
    <VStack align="stretch" gap={4}>
      <HStack justifyContent="space-between" alignItems="center">
        <Box>
          <Text fontSize="lg" fontWeight="700">Metriche custom</Text>
          <Text fontSize="sm" color="gray.500">
            Metriche GPS/carico aggiuntive specifiche per questo team
          </Text>
        </Box>
        <Button
          size="sm"
          variant={showForm ? 'outline' : 'solid'}
          colorPalette={showForm ? 'gray' : 'blue'}
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? 'Annulla' : (
            <HStack gap={1}>
              <PlusIcon size={14} />
              <Text>Nuova metrica</Text>
            </HStack>
          )}
        </Button>
      </HStack>

      {/* Form aggiunta */}
      {showForm && (
        <AddMetricForm
          teamId={teamId}
          onSuccess={() => setShowForm(false)}
        />
      )}

      {/* Lista */}
      {isLoading ? (
        <Box textAlign="center" py={6}><Spinner size="sm" /></Box>
      ) : isError ? (
        <Box p={4} bg="red.50" borderRadius="md">
          <Text fontSize="sm" color="red.600">Errore nel caricamento delle metriche</Text>
        </Box>
      ) : metrics.length === 0 ? (
        <Box bg="gray.50" p={6} borderRadius="md" textAlign="center">
          <Text fontSize="sm" color="gray.400">
            Nessuna metrica custom configurata per questo team.
          </Text>
        </Box>
      ) : (
        <VStack align="stretch" gap={2}>
          {metrics.map((metric) => (
            <HStack
              key={metric.canonicalKey}
              p={3}
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              justifyContent="space-between"
            >
              <Box>
                <HStack gap={2} mb={1}>
                  <Text fontSize="sm" fontWeight="semibold">{metric.label}</Text>
                  <Badge colorPalette="blue" size="sm">{metric.unit}</Badge>
                  <Badge colorPalette="gray" size="sm">{metric.category}</Badge>
                  <Badge
                    colorPalette={metric.level === 'session' ? 'purple' : 'orange'}
                    size="sm"
                  >
                    {metric.level === 'session' ? 'Sessione' : 'Per atleta'}
                  </Badge>
                </HStack>
                <Text fontSize="xs" color="gray.500">
                  canonicalKey: <code>{metric.canonicalKey}</code>
                </Text>
              </Box>
              <Button
                size="xs"
                variant="ghost"
                colorPalette="red"
                onClick={() => removeMutation.mutate(metric.canonicalKey)}
                loading={removeMutation.isPending && removeMutation.variables === metric.canonicalKey}
                title="Rimuovi metrica"
              >
                <TrashIcon size={14} />
              </Button>
            </HStack>
          ))}
        </VStack>
      )}
    </VStack>
  );
};
